import { DashboardTask, EnhancedDashboardTask, CreateTaskRequest, UpdateTaskRequest, DashboardEmail, EnhancedDashboardEmail, EmailReply, DashboardSummary, ChatRequest, ChatResponse, PlannerTask, DashboardClient, SharePointBrowseResponse, SharePointFilePreview, KanbanDealsResponse } from '../types/api';
import { apiClient, GraphApiHelpers } from './apiClient';
import { createRetryWrapper } from '../utils/retryUtils';
import { createErrorHandler } from '../utils/errorHandlingUtils';
import { cacheManager, CACHE_KEYS } from '../utils/cacheUtils';
import { globalAbortManager } from '../utils/abortControllerUtils';

// Export the setAuthToken function for compatibility
export const setAuthToken = (token: string | null) => {
  apiClient.setAuthToken(token);
};

// Create retry wrapper and error handler for this service
const retryWrapper = createRetryWrapper('LexAIService');
const handleError = createErrorHandler('LexAIService');

/**
 * Main service class for LexAI API interactions
 * Provides methods for tasks, emails, dashboard data, and chat functionality
 */
export class LexAIService {

  /**
   * Fetches Microsoft Todo lists from Graph API
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of Todo list objects
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * [
   *   {
   *     "id": "AAMkADhlMzJmNzVjLTQ1ZGQtNGI5ZS1hNzU4LTY0MjEwOWI1ZjE5OQAuAAAAAADdNpHnV7Z4RK1V8vU6WnJYAQCfOJ5xgkZrRZ5VzHnK5QwA",
   *     "displayName": "Tasks",
   *     "isOwner": true,
   *     "isShared": false,
   *     "wellknownListName": "defaultList",
   *     "lastModifiedDateTime": "2024-01-15T10:30:00Z"
   *   }
   * ]
   * ```
   */
  static async getMicrosoftTodoLists(abortSignal?: AbortSignal): Promise<any[]> {
    try {
      return await retryWrapper(
        () => apiClient.get<any[]>('/api/todo/lists'),
        'Fetch Microsoft Todo lists',
        '/api/todo/lists',
        'GET',
        abortSignal
      ) || [];
    } catch (error) {
      handleError(error, 'getMicrosoftTodoLists', '/api/todo/lists', 'GET');
      return [];
    }
  }

  /**
   * Fetches dashboard tasks with optional filtering
   * @param filter - Optional filter string to search tasks
   * @param status - Optional status filter ('Planned', 'In Progress', 'Completed')
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of enhanced dashboard tasks
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * [
   *   {
   *     "id": "task-123",
   *     "task": "Review mortgage application for John Smith",
   *     "description": "Complete due diligence on residential mortgage application",
   *     "dueDate": "2024-01-20",
   *     "status": "In Progress",
   *     "priority": "High",
   *     "assignedTo": "Current User",
   *     "isCompleted": false,
   *     "createdDate": "2024-01-15T09:00:00Z",
   *     "dealType": "Mortgage",
   *     "clientName": "John Smith",
   *     "loanAmount": 500000,
   *     "monthlyPayment": 2500,
   *     "automationPotential": 85,
   *     "dealComplexity": "Medium"
   *   },
   *   {
   *     "id": "task-124",
   *     "task": "Follow up with client on loan approval",
   *     "description": "Send approval notification and next steps",
   *     "dueDate": "2024-01-18",
   *     "status": "Planned",
   *     "priority": "Medium",
   *     "assignedTo": "Sarah Johnson",
   *     "isCompleted": false,
   *     "createdDate": "2024-01-16T14:30:00Z"
   *   }
   * ]
   * ```
   */
  static async getDashboardTasks(filter?: string, status?: string, abortSignal?: AbortSignal): Promise<EnhancedDashboardTask[]> {
    try {
      const params = new URLSearchParams();
      if (filter) params.append('filter', filter);
      if (status) params.append('status', status);

      const url = `/api/chat/dashboard/tasks?${params.toString()}`;
      const cacheKey = `tasks_${filter || 'all'}_${status || 'all'}`;

      // Try to get from cache first
      const cached = cacheManager.get<EnhancedDashboardTask[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<any[]>(url),
        'Fetch dashboard tasks',
        url,
        'GET',
        abortSignal
      );

      // Convert any format to our enhanced format
      const enhancedTasks: EnhancedDashboardTask[] = (response || []).map((task: any, index: number) => ({
        id: task.id || task.Id || `task-${index + 1}`, // Handle various ID formats
        task: task.task || task.Task || task.title || 'Untitled Task',
        description: task.description || task.Description || '',
        dueDate: this.formatDate(task.dueDate || task.DueDate || new Date().toISOString()),
        status: this.normalizeStatus(task.status || task.Status || 'Planned'),
        priority: task.priority || task.Priority || 'Medium',
        assignedTo: task.assignedTo || task.AssignedTo || 'Current User',
        createdDate: task.createdDate || task.CreatedDate || new Date().toISOString(),
        isCompleted: task.isCompleted ?? task.IsCompleted ?? (task.status === 'completed' || task.Status === 'Completed' || task.status === 'completed')
      }));

      // Cache the result for 5 minutes
      cacheManager.set(cacheKey, enhancedTasks, 5 * 60 * 1000);

      return enhancedTasks;
    } catch (error) {
      const errorParams = new URLSearchParams();
      if (filter) errorParams.append('filter', filter);
      if (status) errorParams.append('status', status);
      const errorUrl = `/api/chat/dashboard/tasks?${errorParams.toString()}`;
      handleError(error, 'getDashboardTasks', errorUrl, 'GET');
      return [];
    }
  }

  /**
   * Formats date input to ISO date string (YYYY-MM-DD)
   * @private
   * @param dateInput - Date string or Date object to format
   * @returns ISO date string in YYYY-MM-DD format
   */
  private static formatDate(dateInput: string | Date): string {
    try {
      if (dateInput === 'No due date' || !dateInput) {
        return new Date().toISOString().split('T')[0]; // Default to today
      }
      
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0]; // Default to today if invalid
      }
      
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  }

  /**
   * Normalizes task status to standardized values
   * @private
   * @param status - Raw status string from API
   * @returns Normalized status string ('Planned', 'In Progress', 'Completed')
   */
  private static normalizeStatus(status: string): string {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'pending' || lowerStatus === 'planned') return 'Planned';
    if (lowerStatus === 'completed' || lowerStatus === 'done') return 'Completed';
    if (lowerStatus === 'in progress' || lowerStatus === 'inprogress') return 'In Progress';
    return 'Planned'; // Default
  }

  /**
   * Creates a new task via Graph API
   * @param request - Task creation request data
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to created task or null on failure
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // Request Example
   * {
   *   "title": "Review new loan application",
   *   "description": "Process mortgage application for ABC Corp",
   *   "dueDate": "2024-01-25",
   *   "status": "Planned",
   *   "priority": "High",
   *   "assignedTo": "john.doe@company.com"
   * }
   *
   * // Response Example
   * {
   *   "id": "task-456",
   *   "task": "Review new loan application",
   *   "description": "Process mortgage application for ABC Corp",
   *   "dueDate": "2024-01-25",
   *   "status": "Planned",
   *   "priority": "High",
   *   "assignedTo": "john.doe@company.com",
   *   "isCompleted": false,
   *   "createdDate": "2024-01-17T11:00:00Z",
   *   "dealType": "Commercial Loan",
   *   "automationPotential": 70
   * }
   * ```
   */
  static async createTask(request: CreateTaskRequest, abortSignal?: AbortSignal): Promise<EnhancedDashboardTask | null> {
    try {
      const response = await retryWrapper(
        () => apiClient.post<EnhancedDashboardTask>('/api/chat/dashboard/tasks', request),
        'Create task',
        '/api/chat/dashboard/tasks',
        'POST',
        abortSignal
      );

      // Invalidate task cache on successful creation
      cacheManager.clearPattern('tasks_');

      return response;
    } catch (error) {
      handleError(error, 'createTask', '/api/chat/dashboard/tasks', 'POST');
      return null;
    }
  }

  /**
   * Updates an existing task via Graph API
   * @param taskId - Unique identifier of the task to update
   * @param request - Task update request data
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to updated task or null on failure
   * @throws Error on authentication failure, network issues, or invalid task ID
   *
   * @example
   * ```json
   * // Request Example (PUT /api/chat/dashboard/tasks/task-123)
   * {
   *   "title": "Review mortgage application for John Smith - URGENT",
   *   "status": "In Progress",
   *   "priority": "High",
   *   "isCompleted": false
   * }
   *
   * // Response Example
   * {
   *   "id": "task-123",
   *   "task": "Review mortgage application for John Smith - URGENT",
   *   "description": "Complete due diligence on residential mortgage application",
   *   "dueDate": "2024-01-20",
   *   "status": "In Progress",
   *   "priority": "High",
   *   "assignedTo": "Current User",
   *   "isCompleted": false,
   *   "createdDate": "2024-01-15T09:00:00Z",
   *   "lastModified": "2024-01-17T12:00:00Z"
   * }
   * ```
   */
  static async updateTask(taskId: string, request: UpdateTaskRequest, abortSignal?: AbortSignal): Promise<EnhancedDashboardTask | null> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const url = `/api/chat/dashboard/tasks/${encodedTaskId}`;

      const response = await retryWrapper(
        () => apiClient.put<EnhancedDashboardTask>(url, request),
        'Update task',
        url,
        'PUT',
        abortSignal
      );

      // Invalidate task cache on successful update
      cacheManager.clearPattern('tasks_');

      return response;
    } catch (error) {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const url = `/api/chat/dashboard/tasks/${encodedTaskId}`;
      handleError(error, 'updateTask', url, 'PUT');
      return null;
    }
  }

  /**
   * Deletes a task via Graph API
   * @param taskId - Unique identifier of the task to delete
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to true on success, false on failure
   * @throws Error on authentication failure or network issues
   */
  static async deleteTask(taskId: string, abortSignal?: AbortSignal): Promise<boolean> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const url = `/api/chat/dashboard/tasks/${encodedTaskId}`;

      await retryWrapper(
        () => apiClient.delete(url),
        'Delete task',
        url,
        'DELETE',
        abortSignal
      );

      // Invalidate task cache on successful deletion
      cacheManager.clearPattern('tasks_');

      return true;
    } catch (error) {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const url = `/api/chat/dashboard/tasks/${encodedTaskId}`;
      handleError(error, 'deleteTask', url, 'DELETE');
      return false;
    }
  }

  /**
   * Fetches subtasks for a specific task via Graph API
   * @param taskId - Unique identifier of the parent task
   * @returns Promise resolving to array of subtask objects
   * @throws Error on authentication failure, network issues, or invalid task ID
   */
  static async getTaskSubtasks(taskId: string): Promise<any[]> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const response = await apiClient.get(`/api/chat/dashboard/tasks/${encodedTaskId}/subtasks`);
      return response || [];
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      return [];
    }
  }

  /**
   * Creates a new subtask for a specific task via Graph API
   * @param taskId - Unique identifier of the parent task
   * @param title - Title/content of the new subtask
   * @returns Promise resolving to created subtask or null on failure
   * @throws Error on authentication failure, network issues, or invalid task ID
   */
  static async createTaskSubtask(taskId: string, title: string): Promise<any | null> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const response = await apiClient.post(`/api/chat/dashboard/tasks/${encodedTaskId}/subtasks`, { title });
      return response;
    } catch (error) {
      console.error('Error creating subtask:', error);
      return null;
    }
  }

  /**
   * Updates an existing subtask via Graph API
   * @param taskId - Unique identifier of the parent task
   * @param subtaskId - Unique identifier of the subtask to update
   * @param updates - Object containing the fields to update
   * @returns Promise resolving to updated subtask or null on failure
   * @throws Error on authentication failure, network issues, or invalid IDs
   */
  static async updateTaskSubtask(taskId: string, subtaskId: string, updates: any): Promise<any | null> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const encodedSubtaskId = GraphApiHelpers.encodePathParam(subtaskId);
      const response = await apiClient.put(`/api/chat/dashboard/tasks/${encodedTaskId}/subtasks/${encodedSubtaskId}`, updates);
      return response;
    } catch (error) {
      console.error('Error updating subtask:', error);
      return null;
    }
  }

  /**
   * Deletes a subtask via Graph API
   * @param taskId - Unique identifier of the parent task
   * @param subtaskId - Unique identifier of the subtask to delete
   * @returns Promise resolving to true on success, false on failure
   * @throws Error on authentication failure, network issues, or invalid IDs
   */
  static async deleteTaskSubtask(taskId: string, subtaskId: string): Promise<boolean> {
    try {
      const encodedTaskId = GraphApiHelpers.encodePathParam(taskId);
      const encodedSubtaskId = GraphApiHelpers.encodePathParam(subtaskId);
      await apiClient.delete(`/api/chat/dashboard/tasks/${encodedTaskId}/subtasks/${encodedSubtaskId}`);
      return true;
    } catch (error) {
      console.error('Error deleting subtask:', error);
      return false;
    }
  }

  /**
   * Legacy method for backward compatibility - returns simplified task format
   * @returns Promise resolving to array of simplified dashboard tasks
   * @throws Error on authentication failure or network issues
   * @deprecated Use getDashboardTasks() for enhanced task format
   */
  static async getDashboardTasksLegacy(): Promise<DashboardTask[]> {
    const enhancedTasks = await this.getDashboardTasks();
    return enhancedTasks.map(task => ({
      task: task.task,
      dueDate: task.dueDate,
      status: task.status
    }));
  }

  /**
   * Fetches dashboard emails from Graph API
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of dashboard email summaries
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * [
   *   {
   *     "id": "AAMkADhlMzJmNzVjLTQ1ZGQtNGI5ZS1hNzU4LTY0MjEwOWI1ZjE5OQBGAAAAAADdNpHnV7Z4RK1V8vU6WnJYAQCfOJ5xgkZrRZ5VzHnK5QwAAAAAAEMAACfOJ5xgkZrRZ5VzHnK5QwAAAEJvAAA=",
   *     "subject": "Mortgage Application Inquiry - John Smith",
   *     "sender": "John Smith",
   *     "senderEmail": "john.smith@email.com",
   *     "date": "2024-01-17T10:30:00Z",
   *     "bodyPreview": "Dear Mortgage Advisor, I am interested in refinancing my home loan...",
   *     "isRead": false,
   *     "hasAttachments": true,
   *     "attachments": [
   *       {
   *         "name": "loan_application.pdf",
   *         "size": 2048576,
   *         "contentType": "application/pdf"
   *       }
   *     ],
   *     "conversationId": "AAQkADhlMzJmNzVjLTQ1ZGQtNGI5ZS1hNzU4LTY0MjEwOWI1ZjE5OQAQABAAEKOjBq0=",
   *     "importance": "normal",
   *     "categories": ["Mortgage", "Client Inquiry"]
   *   },
   *   {
   *     "id": "AAMkADhlMzJmNzVjLTQ1ZGQtNGI5ZS1hNzU4LTY0MjEwOWI1ZjE5OQBGAAAAAADdNpHnV7Z4RK1V8vU6WnJYAQCfOJ5xgkZrRZ5VzHnK5QwAAAAAAEMAACfOJ5xgkZrRZ5VzHnK5QwAAAEJvAAB=",
   *     "subject": "Re: Commercial Loan Approval",
   *     "sender": "Bank Operations",
   *     "senderEmail": "operations@bank.com",
   *     "date": "2024-01-17T09:15:00Z",
   *     "bodyPreview": "Your commercial loan application has been approved...",
   *     "isRead": true,
   *     "hasAttachments": false,
   *     "importance": "high",
   *     "categories": ["Commercial Loan", "Approval"]
   *   }
   * ]
   * ```
   */
  static async getDashboardEmails(abortSignal?: AbortSignal): Promise<DashboardEmail[]> {
    try {
      const cacheKey = 'emails_dashboard';

      // Try to get from cache first
      const cached = cacheManager.get<DashboardEmail[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<DashboardEmail[]>('/api/chat/dashboard/emails'),
        'Fetch dashboard emails',
        '/api/chat/dashboard/emails',
        'GET',
        abortSignal
      );

      const emails = response || [];

      // Cache the result for 2 minutes (emails change more frequently)
      cacheManager.set(cacheKey, emails, 2 * 60 * 1000);

      return emails;
    } catch (error) {
      handleError(error, 'getDashboardEmails', '/api/chat/dashboard/emails', 'GET');
      // Return empty array - no fallback data as requested
      return [];
    }
  }

  /**
   * Searches emails by contact email address (from or to)
   * @param email - Email address to search for
   * @param maxResults - Maximum results to return
   * @returns Promise resolving to array of matching emails
   */
  static async searchEmailsByContact(email: string, maxResults: number = 20): Promise<DashboardEmail[]> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const response = await apiClient.get<DashboardEmail[]>(
        `/api/chat/dashboard/emails/search?email=${encodedEmail}&maxResults=${maxResults}`
      );
      return response || [];
    } catch (error) {
      console.error('Error searching emails by contact:', error);
      return [];
    }
  }

  /**
   * Fetches detailed email information including full body via Graph API
   * @param emailId - Unique identifier of the email
   * @returns Promise resolving to enhanced email details or null on failure
   * @throws Error on authentication failure, network issues, or invalid email ID
   */
  static async getEmailDetails(emailId: string): Promise<EnhancedDashboardEmail | null> {
    try {
      const encodedEmailId = encodeURIComponent(emailId);
      const response = await apiClient.get<EnhancedDashboardEmail>(`/api/chat/emails/${encodedEmailId}`);
      return response;
    } catch (error) {
      console.error('Error fetching email details:', error);
      return null;
    }
  }

  /**
   * Fetches all emails in a conversation thread via Graph API
   * @param conversationId - Unique identifier of the email conversation
   * @returns Promise resolving to array of emails in the thread
   * @throws Error on authentication failure, network issues, or invalid conversation ID
   */
  static async getEmailThread(conversationId: string): Promise<EnhancedDashboardEmail[]> {
    try {
      const response = await apiClient.get<EnhancedDashboardEmail[]>(`/api/chat/dashboard/emails/thread/${conversationId}`);
      return response || [];
    } catch (error) {
      console.error('Error fetching email thread:', error);
      return [];
    }
  }

  /**
   * Generates an AI-powered summary of an email
   * @param emailId - Unique identifier of the email to summarize
   * @returns Promise resolving to email summary text
   * @throws Error on authentication failure, network issues, or AI processing failure
   */
  static async generateEmailSummary(emailId: string): Promise<string> {
    try {
      const encodedEmailId = encodeURIComponent(emailId);
      const response = await apiClient.post<{summary: string}>(`/api/chat/emails/${encodedEmailId}/summary`);
      return response.summary;
    } catch (error) {
      console.error('Error generating email summary:', error);
      return 'Unable to generate summary at this time.';
    }
  }

  /**
   * Generates an AI-powered reply suggestion for an email
   * @param emailId - Unique identifier of the email to reply to
   * @returns Promise resolving to suggested reply text
   * @throws Error on authentication failure, network issues, or AI processing failure
   */
  static async generateEmailReply(emailId: string): Promise<string> {
    try {
      const encodedEmailId = encodeURIComponent(emailId);
      const response = await apiClient.post<{reply: string}>(`/api/chat/emails/${encodedEmailId}/reply`);
      return response.reply;
    } catch (error) {
      console.error('Error generating email reply:', error);
      return '';
    }
  }

  /**
   * Sends an email reply via Graph API
   * @param reply - Email reply data including recipient, subject, and body
   * @returns Promise resolving to true on success, false on failure
   * @throws Error on authentication failure, network issues, or invalid reply data
   */
  static async sendEmailReply(reply: EmailReply): Promise<boolean> {
    try {
      await apiClient.post('/api/chat/emails/reply', reply);
      return true;
    } catch (error) {
      console.error('Error sending email reply:', error);
      return false;
    }
  }

  /**
   * Fetches dashboard summary data including task and email statistics
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to dashboard summary or null on failure
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * {
   *   "activeDeals": 12,
   *   "pendingTasks": 8,
   *   "unreadEmails": 5,
   *   "completedToday": 3,
   *   "lastUpdate": "2024-01-17T14:30:00Z"
   * }
   * ```
   */
  static async getDashboardSummary(abortSignal?: AbortSignal): Promise<DashboardSummary | null> {
    try {
      const cacheKey = 'dashboard_summary';

      // Try to get from cache first
      const cached = cacheManager.get<DashboardSummary>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<DashboardSummary>('/api/chat/dashboard/summary'),
        'Fetch dashboard summary',
        '/api/chat/dashboard/summary',
        'GET',
        abortSignal
      );

      // Cache the result for 1 minute (summary data changes frequently)
      if (response) {
        cacheManager.set(cacheKey, response, 1 * 60 * 1000);
      }

      return response;
    } catch (error) {
      handleError(error, 'getDashboardSummary', '/api/chat/dashboard/summary', 'GET');
      return null;
    }
  }

  /**
   * Sends a chat message to the LexAI conversational agent
   * @param request - Chat request containing message and context
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to chat response or null on failure
   * @throws Error on authentication failure, network issues, or AI processing failure
   *
   * @example
   * ```json
   * // Request Example
   * {
   *   "message": "Show me all pending mortgage applications that need review",
   *   "userId": "user-123",
   *   "conversationId": "conv-456",
   *   "userName": "Sarah Johnson",
   *   "userEmail": "sarah.johnson@prosperfinance.com"
   * }
   *
   * // Response Example
   * {
   *   "message": "I found 3 pending mortgage applications that require your review. Here are the details:",
   *   "agentExecutions": [
   *     {
   *       "agentName": "TaskTriageAgent",
   *       "purpose": "Query and filter pending mortgage tasks",
   *       "success": true,
   *       "executionTime": 0.85
   *     },
   *     {
   *       "agentName": "EmailTriageAgent",
   *       "purpose": "Check for related email correspondence",
   *       "success": true,
   *       "executionTime": 0.62
   *     }
   *   ],
   *   "suggestions": [
   *     "Review John Smith's application first - it's overdue by 2 days",
   *     "Schedule follow-up call with ABC Corp representative",
   *     "Update task status for completed reviews"
   *   ],
   *   "requiresFollowUp": false
   * }
   * ```
   */
  static async sendChatMessage(request: ChatRequest, abortSignal?: AbortSignal): Promise<ChatResponse | null> {
    // Use real API
    try {
      const response = await retryWrapper(
        () => apiClient.post<ChatResponse>('/api/chat', request),
        'Send chat message',
        '/api/chat',
        'POST',
        abortSignal
      );
      return response;
    } catch (error) {
      handleError(error, 'sendChatMessage', '/api/chat', 'POST');
      return null;
    }
  }

  /**
   * Fetches Planner tasks from Microsoft Planner for the Deals tab
   * @param planName - Optional: Filter by plan display name (e.g., "Home Loans")
   * @param planId - Optional: Direct plan ID
   * @param maxResults - Maximum number of tasks to return (default: 50)
   * @returns Promise resolving to array of Planner tasks
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * [
   *   {
   *     "id": "task-planner-123",
   *     "title": "Review mortgage application",
   *     "planId": "plan-abc",
   *     "planName": "Home Loans",
   *     "bucketId": "bucket-xyz",
   *     "status": "In Progress",
   *     "percentComplete": 50,
   *     "priority": 5,
   *     "dueDate": "2024-01-25T00:00:00Z",
   *     "createdDate": "2024-01-15T09:00:00Z",
   *     "assignedTo": "john.doe@company.com"
   *   }
   * ]
   * ```
   */
  static async getDashboardPlannerTasks(
    planName?: string,
    planId?: string,
    maxResults: number = 50
  ): Promise<PlannerTask[]> {
    try {
      const params = new URLSearchParams();
      if (planName) params.append('planName', planName);
      if (planId) params.append('planId', planId);
      params.append('maxResults', maxResults.toString());

      const url = `/api/chat/dashboard/planner-tasks?${params.toString()}`;
      const response = await apiClient.get<PlannerTask[]>(url);
      return response || [];
    } catch (error) {
      console.error('Error fetching planner tasks:', error);
      return [];
    }
  }

  /**
   * Fetches clients from Mercury CRM
   * @param search - Optional: Filter by client name
   * @param maxResults - Maximum number of clients to return (default: 25)
   * @returns Promise resolving to array of dashboard clients
   * @throws Error on authentication failure or network issues
   *
   * @example
   * ```json
   * // API Response Example
   * [
   *   {
   *     "id": "client-123",
   *     "name": "John Smith",
   *     "email": "john.smith@email.com",
   *     "phone": "+61 400 123 456",
   *     "status": "Active"
   *   },
   *   {
   *     "id": "client-456",
   *     "name": "Jane Doe",
   *     "email": "jane.doe@email.com",
   *     "phone": "+61 400 789 012",
   *     "status": "Lead"
   *   }
   * ]
   * ```
   */
  static async getDashboardClients(
    search?: string,
    maxResults: number = 25
  ): Promise<DashboardClient[]> {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('maxResults', maxResults.toString());

      const url = `/api/chat/dashboard/clients?${params.toString()}`;
      const response = await apiClient.get<DashboardClient[]>(url);
      return response || [];
    } catch (error) {
      console.error('Error fetching clients:', error);
      return [];
    }
  }

  /**
   * Fetches clients from the SharePoint sandbox folder.
   * These are client folders in the LexAI sandbox for testing.
   *
   * @returns Promise resolving to an array of sandbox clients
   *
   * @example
   * ```json
   * [
   *   {
   *     "id": "sandbox-client-0",
   *     "name": "Baker - Luke and Tarryn",
   *     "status": "Sandbox",
   *     "source": "SharePoint"
   *   },
   *   {
   *     "id": "sandbox-client-1",
   *     "name": "Smith - John",
   *     "status": "Sandbox",
   *     "source": "SharePoint"
   *   }
   * ]
   * ```
   */
  static async getSandboxClients(): Promise<DashboardClient[]> {
    try {
      const response = await apiClient.get<DashboardClient[]>('/api/chat/dashboard/sandbox/clients');
      return response || [];
    } catch (error) {
      console.error('Error fetching sandbox clients:', error);
      return [];
    }
  }

  /**
   * Fetches individual people from all client.md files in the sandbox.
   * Returns detailed person information parsed from markdown files.
   * This is the preferred method for getting people for the sidebar.
   *
   * @returns Promise resolving to array of people with their details
   */
  static async getSandboxPeople(): Promise<DashboardClient[]> {
    try {
      const response = await apiClient.get<DashboardClient[]>('/api/chat/dashboard/sandbox/people');
      return response || [];
    } catch (error) {
      console.error('Error fetching sandbox people:', error);
      return [];
    }
  }

  /**
   * Fetches deals (subfolders) for a specific client in the SharePoint sandbox.
   *
   * @param clientName - The name of the client folder
   * @returns Promise resolving to an array of deal folders
   */
  static async getSandboxClientDeals(clientName: string): Promise<{ id: string; name: string; clientName: string; status: string }[]> {
    try {
      const response = await apiClient.get<{ id: string; name: string; clientName: string; status: string }[]>(
        `/api/chat/dashboard/sandbox/clients/${encodeURIComponent(clientName)}/deals`
      );
      return response || [];
    } catch (error) {
      console.error('Error fetching sandbox client deals:', error);
      return [];
    }
  }

  /**
   * Syncs a client folder with Mercury CRM data by creating/updating client.md
   * Uses the chat endpoint to trigger the SandboxSync plugin
   *
   * @param clientName - The name of the client folder to sync
   * @returns Promise resolving to the sync result message
   *
   * @example
   * ```typescript
   * const result = await LexAIService.syncClientWithMercury('Baker - Luke and Tarryn');
   * // Result: "✅ Client Folder Setup Complete..."
   * ```
   */
  static async syncClientWithMercury(clientName: string, forceRefresh: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      // Call the dedicated sync endpoint directly (bypasses LLM)
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        wasCreated?: boolean;
        wasUpdated?: boolean;
        filePath?: string;
        error?: string;
      }>('/api/sync/client', {
        clientFolderName: clientName,
        forceRefresh
      });

      if (!response) {
        return {
          success: false,
          message: 'No response from server'
        };
      }

      return {
        success: response.success,
        message: response.message || (response.success ? 'Sync completed' : 'Sync failed')
      };
    } catch (error) {
      console.error('Error syncing client with Mercury:', error);
      return {
        success: false,
        message: `Failed to sync client: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Syncs all clients in the sandbox with Mercury CRM data
   * Uses the batch sync endpoint for better performance
   *
   * @param clientNames - Array of client folder names (used for progress callback)
   * @param onProgress - Optional callback for progress updates
   * @param forceRefresh - Force refresh even if files exist
   * @returns Promise resolving to sync results for all clients
   */
  static async syncAllClientsWithMercury(
    clientNames: string[],
    onProgress?: (current: number, total: number, clientName: string) => void,
    forceRefresh: boolean = false
  ): Promise<{ clientName: string; success: boolean; message: string }[]> {
    try {
      // Call batch sync endpoint
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        totalFolders: number;
        successCount: number;
        failCount: number;
        results: Array<{
          clientFolderName: string;
          success: boolean;
          message: string;
          wasCreated?: boolean;
          wasUpdated?: boolean;
          error?: string;
        }>;
      }>('/api/sync/all-clients', { forceRefresh });

      if (!response || !response.results) {
        return clientNames.map(clientName => ({
          clientName,
          success: false,
          message: 'No response from server'
        }));
      }

      // Map results to expected format and call progress for UI update
      return response.results.map((r, index) => {
        onProgress?.(index + 1, response.results.length, r.clientFolderName);
        return {
          clientName: r.clientFolderName,
          success: r.success,
          message: r.message || (r.success ? 'Synced' : 'Failed')
        };
      });
    } catch (error) {
      console.error('Error syncing all clients with Mercury:', error);
      return clientNames.map(clientName => ({
        clientName,
        success: false,
        message: `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
    }
  }

  // ==================== DEAL SYNC ====================

  /**
   * Syncs deal.md files for all deal folders within a specific client folder.
   */
  static async syncClientDeals(
    clientName: string,
    forceRefresh: boolean = false
  ): Promise<{
    success: boolean;
    message: string;
    results: Array<{ dealFolderName: string; success: boolean; message: string }>;
  }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        clientFolder: string;
        totalDeals: number;
        successCount: number;
        failCount: number;
        results: Array<{
          dealFolderName: string;
          success: boolean;
          message: string;
          wasCreated?: boolean;
          wasUpdated?: boolean;
        }>;
      }>('/api/sync/client-deals', {
        clientFolderName: clientName,
        forceRefresh
      });

      return {
        success: response?.success ?? false,
        message: response?.message ?? 'No response',
        results: response?.results ?? []
      };
    } catch (error) {
      console.error('Error syncing client deals:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        results: []
      };
    }
  }

  /**
   * Syncs deal.md files for ALL clients and their deal folders.
   */
  static async syncAllDeals(
    forceRefresh: boolean = false
  ): Promise<{
    success: boolean;
    message: string;
    results: Array<{ clientFolder: string; dealFolder: string; success: boolean; message: string }>;
  }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        totalSuccess: number;
        totalFail: number;
        results: Array<{
          clientFolder: string;
          dealFolder: string;
          success: boolean;
          message: string;
        }>;
      }>('/api/sync/all-deals', { forceRefresh });

      return {
        success: response?.success ?? false,
        message: response?.message ?? 'No response',
        results: response?.results ?? []
      };
    } catch (error) {
      console.error('Error syncing all deals:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        results: []
      };
    }
  }

  // ==================== KANBAN DEALS ====================

  /**
   * Fetches all deals across all clients for Kanban board display.
   */
  static async getKanbanDeals(abortSignal?: AbortSignal, skipCache?: boolean): Promise<KanbanDealsResponse | null> {
    try {
      const cacheKey = 'kanban_deals';
      if (!skipCache) {
        const cached = cacheManager.get<KanbanDealsResponse>(cacheKey);
        if (cached) return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<KanbanDealsResponse>('/api/chat/dashboard/sandbox/kanban-deals'),
        'Fetch kanban deals',
        '/api/chat/dashboard/sandbox/kanban-deals',
        'GET',
        abortSignal
      );

      if (response) {
        cacheManager.set(cacheKey, response, 5 * 60 * 1000);
      }

      return response;
    } catch (error) {
      handleError(error, 'getKanbanDeals', '/api/chat/dashboard/sandbox/kanban-deals', 'GET');
      return null;
    }
  }

  /**
   * Updates a deal's status in its deal.md file (used by Kanban drag-and-drop)
   */
  static async updateDealStatus(clientFolder: string, dealFolder: string, newStatus: string): Promise<boolean> {
    try {
      const response = await apiClient.put<{ success: boolean }>('/api/chat/dashboard/sandbox/deal-status', {
        clientFolder,
        dealFolder,
        newStatus,
      });
      return response?.success ?? false;
    } catch (error) {
      handleError(error, 'updateDealStatus', '/api/chat/dashboard/sandbox/deal-status', 'PUT');
      throw error;
    }
  }

  // ==================== SHAREPOINT FILE BROWSING ====================

  /**
   * Browses SharePoint sandbox folder contents with breadcrumb navigation
   * @param path - Relative path within the sandbox (empty = root)
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Browse response with breadcrumbs and items
   */
  static async browseSharePoint(path?: string, abortSignal?: AbortSignal): Promise<SharePointBrowseResponse | null> {
    try {
      const params = new URLSearchParams();
      if (path) params.append('path', path);

      const url = `/api/sync/browse?${params.toString()}`;
      const cacheKey = `sp_browse_${path || 'root'}`;

      const cached = cacheManager.get<SharePointBrowseResponse>(cacheKey);
      if (cached) return cached;

      const response = await retryWrapper(
        () => apiClient.get<SharePointBrowseResponse>(url),
        'Browse SharePoint folder',
        url,
        'GET',
        abortSignal
      );

      if (response) {
        cacheManager.set(cacheKey, response, 2 * 60 * 1000);
      }

      return response;
    } catch (error) {
      handleError(error, 'browseSharePoint', `/api/sync/browse`, 'GET');
      return null;
    }
  }

  /**
   * Gets file preview metadata or content from SharePoint
   * @param path - Relative path to the file within the sandbox
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns File preview data including type and optional content
   */
  static async getSharePointFilePreview(path: string, abortSignal?: AbortSignal): Promise<SharePointFilePreview | null> {
    try {
      const url = `/api/sync/file-preview?path=${encodeURIComponent(path)}`;

      const response = await retryWrapper(
        () => apiClient.get<SharePointFilePreview>(url),
        'Get SharePoint file preview',
        url,
        'GET',
        abortSignal
      );

      return response;
    } catch (error) {
      handleError(error, 'getSharePointFilePreview', `/api/sync/file-preview`, 'GET');
      return null;
    }
  }

  /**
   * Performs a health check on the LexAI backend services
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to true if services are healthy, false otherwise
   * @throws Error on network issues (service down)
   *
   * @example
   * ```json
   * // Success Response (HTTP 200)
   * {
   *   "status": "healthy",
   *   "timestamp": "2024-01-17T14:30:00Z",
   *   "services": {
   *     "database": "connected",
   *     "graphApi": "accessible",
   *     "aiEngine": "operational"
   *   }
   * }
   *
   * // Error Response (HTTP 503)
   * {
   *   "status": "unhealthy",
   *   "timestamp": "2024-01-17T14:30:00Z",
   *   "error": "Database connection failed"
   * }
   * ```
   */
  static async checkHealth(abortSignal?: AbortSignal): Promise<boolean> {
    try {
      await retryWrapper(
        () => apiClient.get('/api/chat/health'),
        'Health check',
        '/api/chat/health',
        'GET',
        abortSignal
      );
      return true;
    } catch (error) {
      handleError(error, 'checkHealth', '/api/chat/health', 'GET');
      return false;
    }
  }
}

export default LexAIService;
